import type { RuntimeAuthorityRenewalState } from "../../environments/runtime/cloudRuntimeAuthorityRenewal";
import { DiagnosticsSection, StatusPill, formatTimestamp } from "./DiagnosticsPanelPrimitives";

function authorityPresentation(state: RuntimeAuthorityRenewalState | null): {
  readonly label: string;
  readonly tone: "ready" | "warning" | "error" | "muted";
  readonly detail: string;
} {
  if (state === null) {
    return {
      label: "Not observed",
      tone: "muted",
      detail: "Connect to this Environment to establish and monitor provider authority.",
    };
  }
  if (state.kind === "scheduled") {
    return {
      label: "Live",
      tone: "ready",
      detail: `Expires ${formatTimestamp(state.expiresAt)} · renews ${formatTimestamp(state.renewAt)}`,
    };
  }
  if (state.kind === "renewing") {
    return {
      label: "Renewing",
      tone: "warning",
      detail: `Refreshing authority before ${formatTimestamp(state.expiresAt)}. Transport remains connected.`,
    };
  }
  if (state.kind === "ready") {
    return {
      label: "Renewed",
      tone: "ready",
      detail: `Renewed ${formatTimestamp(state.renewedAt)} · expires ${formatTimestamp(state.expiresAt)}`,
    };
  }
  return state.code === "authority_auth_rejected"
    ? {
        label: "Reauthentication required",
        tone: "error",
        detail: "CMMD rejected authority renewal. Provider delivery is unavailable.",
      }
    : {
        label: "Retrying",
        tone: "warning",
        detail: state.retryAt
          ? `Renewal failed safely. Next attempt ${formatTimestamp(state.retryAt)}.`
          : "Renewal failed safely. Provider delivery is unavailable.",
      };
}

export function DiagnosticsProviderAuthority({
  state,
}: {
  readonly state: RuntimeAuthorityRenewalState | null;
}) {
  const presentation = authorityPresentation(state);
  return (
    <DiagnosticsSection
      title="Provider authority"
      id="diagnostics-authority"
      description="Short-lived exact-machine authority for provider delivery. This is separate from WebSocket transport health."
      action={<StatusPill label={presentation.label} tone={presentation.tone} />}
    >
      <p className="px-3 text-sm text-muted-foreground sm:px-4">{presentation.detail}</p>
    </DiagnosticsSection>
  );
}
